import React, {useRef, useState} from 'react';
import {updateAiContentLoadingHours, updateAiHumanOnlyHours} from "../../../../../../../services/company/api";
import useAvailableHeight from "../../../../../../../utils/hooks/useAvailableHeight";
import AiSupportedLanguages from "./components/AI/AiSupportedLanguages";
import AiTypeSelector from "./components/AI/AiTypeSelector";
import AiDescriptionEditor from "./components/AI/AiDescriptionEditor";
import AiNumberSetting from "./components/AI/AiNumberSetting";
import AiApiKey from "./components/AI/AiApiKey";
import AiStyle from "./components/AI/AiStyle";
import AiKnowledgeSources from "./components/AI/AiKnowledgeSources";
import AiKnowledgeIndex from "./components/AI/AiKnowledgeIndex";
import Translate from "../../../../../../../components/Translate";
import config from "../../../../../../../config";
import "./components/AI/AiConfigs.css";

const AiConfigs = ({
                       addNotification,
                       setAiType,
                       aiSupportedLanguages,
                       setAiSupportedLanguages,
                       aiType,
                       features,
                       user,
                       client,
                       aiDescription,
                       handleAiDescriptionChange,
                       handleSaveAiDescription,
                       aiContentLoadingHours,
                       setAiContentLoadingHours,
                       aiHumanOnlyHours,
                       setAiHumanOnlyHours
                   }) => {

    // the child blocks each return null for an unset type; checking here too lets
    // us say why the rest of the form is missing instead of showing blank space
    const isConfigurable = aiType === config.AITypes.GPT_AI
        || aiType === config.AITypes.GEMINI_AI
        || aiType === config.AITypes.TEAM_AI
        || aiType === config.AITypes.TEAM_AI_TEST;

    const hasSelectableTypes = features.includes(config.features.GPT_AI)
        || features.includes(config.features.GEMINI_AI)
        || features.includes(config.features.TEAM_AI);

    // adding or removing a source changes the counters in the block below it, and
    // the two are siblings that cannot see each other; bumping this tells the
    // knowledge block to read its numbers again
    const [knowledgeVersion, setKnowledgeVersion] = useState(0);
    const onKnowledgeChanged = () => setKnowledgeVersion(v => v + 1);

    const containerRef = useRef(null);
    // A fixed height rather than a cap: with no AI type selected there is almost
    // nothing to show, and a block that collapsed to its content let the page
    // scroll instead of the block. The panel now occupies the same band either
    // way, and is the only thing that scrolls. Remeasured when the type changes,
    // since that adds and removes whole blocks.
    const availableHeight = useAvailableHeight(containerRef, [aiType]);

    return (
        <div
            className="ai-config"
            ref={containerRef}
            style={availableHeight ? {height: availableHeight + 'px'} : undefined}
        >
            {/* both are short controls — they sit side by side instead of
                taking a full-width card each */}
            <div className="ai-row ai-row--top">
                <AiTypeSelector
                    addNotification={addNotification}
                    client={client}
                    aiType={aiType}
                    setAiType={setAiType}
                    user={user}
                    features={features}
                />

                <AiSupportedLanguages
                    addNotification={addNotification}
                    aiType={aiType}
                    aiSupportedLanguages={aiSupportedLanguages}
                    setAiSupportedLanguages={setAiSupportedLanguages}
                    client={client}
                    user={user}
                />
            </div>

            {!isConfigurable && hasSelectableTypes && (
                <div className="ai-empty">
                    <Translate text="selectAiTypeFirst"/>
                </div>
            )}

            {/* the tariff has AI but no provider to run it on - nothing on this
                tab can be filled in, and that is not the operator's doing */}
            {!hasSelectableTypes && (
                <div className="ai-empty">
                    <Translate text="noAiTypesInTariff"/>
                </div>
            )}

            <AiApiKey
                client={client}
                aiType={aiType}
                user={user}
                addNotification={addNotification}
            />
            
            <AiStyle
                client={client}
                aiType={aiType}
                user={user}
                addNotification={addNotification}
            />

            <AiDescriptionEditor
                addNotification={addNotification}
                aiType={aiType}
                aiDescription={aiDescription}
                handleAiDescriptionChange={handleAiDescriptionChange}
                handleSaveAiDescription={handleSaveAiDescription}
                user={user}
            />

            <AiKnowledgeSources
                client={client}
                aiType={aiType}
                user={user}
                onKnowledgeChanged={onKnowledgeChanged}
                addNotification={addNotification}
            />

            <AiKnowledgeIndex
                client={client}
                aiType={aiType}
                user={user}
                aiSupportedLanguages={aiSupportedLanguages}
                knowledgeVersion={knowledgeVersion}
                addNotification={addNotification}
            />

            <div className="ai-row">
                <AiNumberSetting
                    min={0}
                    max={72}
                    addNotification={addNotification}
                    titleKey="aiContentLoadingHours"
                    labelKey="aiContentLoadingHoursExpl"
                    aiType={aiType}
                    value={aiContentLoadingHours}
                    setValue={setAiContentLoadingHours}
                    updateFunction={updateAiContentLoadingHours}
                    client={client}
                />
                <AiNumberSetting
                    min={0}
                    max={24}
                    addNotification={addNotification}
                    titleKey="aiHumanOnlyHours"
                    labelKey="aiHumanOnlyHoursExpl"
                    aiType={aiType}
                    value={aiHumanOnlyHours}
                    setValue={setAiHumanOnlyHours}
                    updateFunction={updateAiHumanOnlyHours}
                    client={client}
                />
            </div>
        </div>
    );
};

export default AiConfigs;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/configurations/AIConfigs.jsx